import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Configure axios defaults
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear invalid token
      localStorage.removeItem('token');
    }
    console.error('Response error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

interface Company {
  id: number;
  name: string;
}

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  companyId: number;
  company?: Company;
  role: string;
  anomalyNotificationsEnabled?: boolean;
  anomalyNotificationFrequency?: 'weekly' | 'biweekly' | 'monthly';
  anomalyBodaccFamilies?: string[] | null;
  prospectionNotificationsEnabled?: boolean;
  prospectionNotificationFrequency?: 'weekly' | 'biweekly' | 'monthly';
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  isLoading: false,
  error: null,
};

// Restore user session
export const restoreSession = createAsyncThunk(
  'auth/restoreSession',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return rejectWithValue('No token found');
      }

      const response = await api.get('/auth/me');
      return response.data;
    } catch (error: any) {
      localStorage.removeItem('token');
      return rejectWithValue(
        error.response?.data?.message || 'Votre session a expiré, veuillez vous reconnecter.'
      );
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      console.log('Attempting login with:', { email: credentials.email });
      const response = await api.post('/auth/login', credentials);
      console.log('Login response:', response.data);
      localStorage.setItem('token', response.data.token);
      return response.data;
    } catch (error: any) {
      console.error('Login error:', error.response?.data || error.message);
      return rejectWithValue(
        error.response?.data?.message || 'Une erreur est survenue lors de la connexion'
      );
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (
    userData: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      company: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post('/auth/register', userData);
      localStorage.setItem('token', response.data.token);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Une erreur est survenue lors de l\'inscription'
      );
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (
    updateData: {
      firstName: string;
      lastName: string;
      company: string;
      currentPassword?: string;
      newPassword?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.put('/auth/profile', updateData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Une erreur est survenue lors de la mise à jour du profil'
      );
    }
  }
);

export const impersonateUser = createAsyncThunk(
  'auth/impersonate',
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await api.post(`/admin/users/${userId}/impersonate`);
      
      // Set token in localStorage
      localStorage.setItem('token', response.data.token);
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Une erreur est survenue lors de l\'impersonation'
      );
    }
  }
);

export const updateNotificationPreferences = createAsyncThunk(
  'auth/updateNotificationPreferences',
  async (
    preferences: {
      anomalyNotificationsEnabled?: boolean;
      anomalyNotificationFrequency?: 'weekly' | 'biweekly' | 'monthly';
      anomalyBodaccFamilies?: string[] | null;
      prospectionNotificationsEnabled?: boolean;
      prospectionNotificationFrequency?: 'weekly' | 'biweekly' | 'monthly';
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.put('/auth/notification-preferences', preferences);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Une erreur est survenue lors de la mise à jour des préférences de notification'
      );
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      localStorage.removeItem('token');
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Register cases
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Impersonate cases
      .addCase(impersonateUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(impersonateUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(impersonateUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update profile cases
      .addCase(updateProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.error = null;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Restore session cases
      .addCase(restoreSession.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.error = null;
      })
      .addCase(restoreSession.rejected, (state, action) => {
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.error = action.payload as string;
      })
      // Update notification preferences cases
      .addCase(updateNotificationPreferences.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateNotificationPreferences.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.user) {
          state.user = {
            ...state.user,
            anomalyNotificationsEnabled: action.payload.anomalyNotificationsEnabled,
            anomalyNotificationFrequency: action.payload.anomalyNotificationFrequency,
            anomalyBodaccFamilies: action.payload.anomalyBodaccFamilies,
            prospectionNotificationsEnabled: action.payload.prospectionNotificationsEnabled,
            prospectionNotificationFrequency: action.payload.prospectionNotificationFrequency,
          };
        }
        state.error = null;
      })
      .addCase(updateNotificationPreferences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer; 